/* -------------------------------------------------------------------------- */
/* Money — the canonical wire representation, shared by the API and both apps   */
/* -------------------------------------------------------------------------- */

import { currencyFormatOptions, currencyScale } from "./currency";

/**
 * Money crosses the wire as a **canonical decimal string**, never as a `number`.
 *
 * IEEE-754 cannot represent most decimal fractions, so a JSON `number` is a lossy container for money in BOTH
 * directions: `9007199254740993.01` survives `Intl.NumberFormat().format()` intact as a string and comes out as
 * `…994.00` as a float. A dollar vanishes, silently, with no error anywhere.
 *
 * The branding is what makes that rule enforceable: a plain `string` cannot be assigned to a `MoneyString`, so a
 * value has to pass through `parseMoney`/`fromMinorUnits` — the two places that guarantee the canonical shape.
 */
export type MoneyString = string & { readonly __brand: "MoneyString" };

/**
 * A plain decimal string: optional sign, integer part, optional fraction. No exponent, no thousands separator,
 * no currency symbol, no whitespace.
 *
 * Deliberately does NOT pin the number of decimals — the scale is a property of the currency, not of money, so
 * that check lives in `parseMoney(value, currency)` where the currency is actually known.
 */
export const MONEY_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;

/** Whether a raw string is shaped like money at all (says nothing about its scale — see `isCanonicalMoney`). */
export const isMoneyString = (value: string): boolean => MONEY_PATTERN.test(value);

/** Whether a string is money AND carries exactly the decimals its currency has. This is the wire contract. */
export const isCanonicalMoney = (value: string, currency: string): boolean => {
  if (!isMoneyString(value)) return false;
  const scale = currencyScale(currency);
  const fraction = value.includes(".") ? value.split(".")[1]!.length : 0;
  return fraction === scale;
};

/** Thrown when a value cannot be money. Never rounded away: silently rounding input is how a receipt stops matching the deposit. */
export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MoneyError";
  }
}

/**
 * The exact integer number of minor units (cents, centimes, francs) a money string represents.
 *
 * This is the only arithmetic substrate in this module. Every operation below is integer `bigint` math on minor
 * units, so it is exact by construction — there is no rounding to reason about and no epsilon to tune.
 *
 * **Excess precision throws.** `"10.005"` is not 1000 or 1001 cents; it is not a USD amount at all, and guessing
 * which one the user meant is exactly the class of silent corruption this module exists to end.
 */
export const toMinorUnits = (value: string, currency: string): bigint => {
  if (!isMoneyString(value)) {
    throw new MoneyError(`Not a money value: ${JSON.stringify(value)}`);
  }

  const scale = currencyScale(currency);
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [whole, fraction = ""] = unsigned.split(".");

  if (fraction.length > scale) {
    throw new MoneyError(
      `${value} has ${fraction.length} decimal places but ${currency} has ${scale} — refusing to round away real precision`
    );
  }

  const minor = BigInt(whole! + fraction.padEnd(scale, "0"));
  return negative ? -minor : minor;
};

/** The canonical money string for an exact number of minor units. The inverse of `toMinorUnits`. */
export const fromMinorUnits = (minor: bigint, currency: string): MoneyString => {
  const scale = currencyScale(currency);
  const negative = minor < 0n;
  const digits = (negative ? -minor : minor).toString().padStart(scale + 1, "0");

  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale === 0 ? "" : `.${digits.slice(digits.length - scale)}`;

  return `${negative ? "-" : ""}${whole}${fraction}` as MoneyString;
};

/**
 * Normalizes any accepted money input into the canonical string for `currency`.
 *
 * A `number` is accepted only as a convenience at the edges of code that has not migrated yet, and it is
 * converted through its decimal representation rather than by arithmetic — but it can still only carry what a
 * float could carry, so it is a lossy doorway, not a safe one. Prefer passing strings.
 */
export const parseMoney = (value: string | number | bigint, currency: string): MoneyString => {
  if (typeof value === "bigint") return fromMinorUnits(value, currency);

  const raw = typeof value === "number" ? numberToDecimalString(value) : value.trim();
  return fromMinorUnits(toMinorUnits(raw, currency), currency);
};

/** `1e-7` and `1e21` are valid JS numbers and invalid money strings — reject them rather than let `String()` lie. */
const numberToDecimalString = (value: number): string => {
  if (!Number.isFinite(value)) {
    throw new MoneyError(`Not a money value: ${value}`);
  }
  const text = String(value);
  if (!isMoneyString(text)) {
    throw new MoneyError(`Cannot read ${text} as money — exponential notation is not a money value`);
  }
  return text;
};

/** Rounds a money value to its currency's scale, HALF-UP (away from zero) — the same rule the API's Decimal layer uses. */
export const roundToCurrencyScale = (value: string, currency: string): MoneyString => {
  if (!isMoneyString(value)) {
    throw new MoneyError(`Not a money value: ${JSON.stringify(value)}`);
  }

  const scale = currencyScale(currency);
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [whole, fraction = ""] = unsigned.split(".");

  if (fraction.length <= scale) return parseMoney(value, currency);

  // Round half away from zero: look at the first dropped digit only — the rest cannot change the outcome.
  const kept = BigInt(whole! + fraction.slice(0, scale));
  const roundUp = Number(fraction[scale]) >= 5;
  const minor = kept + (roundUp ? 1n : 0n);

  return fromMinorUnits(negative ? -minor : minor, currency);
};

/** −1 / 0 / 1, comparing exactly. Never `a - b < epsilon`. */
export const compareMoney = (a: string, b: string, currency: string): number => {
  const left = toMinorUnits(a, currency);
  const right = toMinorUnits(b, currency);
  return left < right ? -1 : left > right ? 1 : 0;
};

export const isZeroMoney = (value: string, currency: string): boolean =>
  toMinorUnits(value, currency) === 0n;

/** The zero of a currency, at its scale: `"0.00"` for USD, `"0"` for RWF. */
export const zeroMoney = (currency: string): MoneyString => fromMinorUnits(0n, currency);

/**
 * Splits `amount` into parts weighted by integer basis-point `shares`, so the parts sum **exactly** to `amount`.
 *
 * Each part but the last is `amount × shareᵢ / Σshares`, rounded half-up and clamped to what is left; the final
 * part absorbs the remainder. Because it runs on integer minor units there is no drift to sweep up — the
 * remainder is exact, not an epsilon.
 *
 * This is the one function that must produce the same answer in the browser preview and in the server write. The
 * API's `splitAmountByBasisPoints` delegates here precisely so "provably the same function" is a fact about the
 * code rather than a claim in a comment.
 */
export const splitByBasisPoints = (
  amount: string,
  shares: number[],
  currency: string
): MoneyString[] => {
  if (shares.length === 0) return [];

  const total = toMinorUnits(amount, currency);
  const totalBp = shares.reduce((sum, bp) => sum + bp, 0);

  // Degenerate input (no positive weight, or nothing to split): everything on the last part, so the sum reconciles.
  if (totalBp <= 0 || total === 0n) {
    return shares.map((_, index) =>
      fromMinorUnits(index === shares.length - 1 ? total : 0n, currency)
    );
  }

  const denominator = BigInt(totalBp);
  let allocated = 0n;

  return shares.map((bp, index) => {
    if (index === shares.length - 1) {
      return fromMinorUnits(total - allocated, currency);
    }

    const proposed = divideHalfUp(total * BigInt(bp), denominator);
    const remaining = total - allocated;
    const next = absBigInt(proposed) > absBigInt(remaining) ? remaining : proposed;

    allocated += next;
    return fromMinorUnits(next, currency);
  });
};

/** A whole share, in basis points. Every allocation below sums to exactly this — never 99.9%, never 100.1%. */
export const BASIS_POINTS_TOTAL = 10_000;

/**
 * The inverse of `splitByBasisPoints`: given the parts, what share of the whole is each one?
 *
 * Returns each weight's share in **integer basis points** (0–10000), allocated by the largest-remainder
 * (Hamilton) method so the shares sum to **exactly** `BASIS_POINTS_TOTAL` — three equal categories come back as
 * 3334/3333/3333, not as three 33.33%s that add up to 99.99%. Rounding each share independently (what
 * `Math.round(value / total * 100)` does in a browser) cannot make that guarantee, and a donut whose slices do
 * not sum to 100% is a donut nobody can reconcile.
 *
 * Only POSITIVE weights take part: a zero or negative weight has no share of a positive total and gets `0`. If
 * nothing is positive there is no whole to divide, and every share is `0` (the sum is 0, not 10000 — inventing a
 * 100% out of nothing would be the lie this function exists to prevent).
 *
 * The weights are integers, so the arithmetic is exact. They need not be minor units: a share is a RATIO, so any
 * common scale gives the same answer — see `allocateMoneySharesInBasisPoints` for the money-string form.
 */
export const allocateBasisPoints = (weights: bigint[]): number[] => {
  const positive = weights.map((weight) => (weight > 0n ? weight : 0n));
  const total = positive.reduce((sum, weight) => sum + weight, 0n);
  if (total <= 0n) return weights.map(() => 0);

  const scale = BigInt(BASIS_POINTS_TOTAL);

  // Floor first — the floors can only under-allocate — then hand the leftover basis points to the largest
  // remainders. `remainder` is the exact numerator of the dropped fraction, so the ordering is exact too.
  const floors = positive.map((weight, index) => {
    const numerator = weight * scale;
    return {
      index,
      share: Number(numerator / total),
      remainder: numerator % total,
    };
  });

  const shares = floors.map((entry) => entry.share);
  const allocated = shares.reduce((sum, share) => sum + share, 0);

  // Only weights that actually carry value may receive a leftover point. Handing one to a zero-weight category
  // would print "0.01%" next to an amount of zero.
  const candidates = floors
    .filter((entry) => positive[entry.index]! > 0n)
    // Ties break on the earlier index, so the allocation is deterministic for identical weights.
    .sort((left, right) => {
      if (left.remainder !== right.remainder) return left.remainder > right.remainder ? -1 : 1;
      return left.index - right.index;
    });

  for (let step = 0; step < BASIS_POINTS_TOTAL - allocated; step += 1) {
    const target = candidates[step % candidates.length]!;
    shares[target.index] = (shares[target.index] ?? 0) + 1;
  }

  return shares;
};

/** `allocateBasisPoints` for canonical money strings — the shares of a set of amounts in one currency. */
export const allocateMoneySharesInBasisPoints = (amounts: string[], currency: string): number[] =>
  allocateBasisPoints(amounts.map((amount) => toMinorUnits(amount, currency)));

const absBigInt = (value: bigint): bigint => (value < 0n ? -value : value);

/** Integer division rounding halves away from zero — Decimal's `ROUND_HALF_UP`, so the two layers agree. */
const divideHalfUp = (numerator: bigint, denominator: bigint): bigint => {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  if (absBigInt(remainder) * 2n < absBigInt(denominator)) return quotient;
  return numerator < 0n ? quotient - 1n : quotient + 1n;
};

/**
 * Formats money for display. `Intl` parses a decimal string exactly and already knows every currency's minor
 * units, so the browser needs no decimal library to *show* money correctly — it only ever needed to stop
 * converting it to a float first.
 *
 * Note there is deliberately no `maximumFractionDigits` here: passing `2` is what printed RWF as fractional
 * francs. `Intl` gets the scale right on its own for every ISO-4217 currency.
 */
export const formatMoney = (
  value: string,
  currency: string,
  locale: string = "en-US",
  options: Omit<Intl.NumberFormatOptions, "style" | "currency"> = {}
): string =>
  new Intl.NumberFormat(locale, { ...currencyFormatOptions(currency), currency, ...options }).format(
    // `format` accepts a string and parses it exactly — the whole point. The cast is for TS's stale lib types,
    // which only learned about string input in ES2023.
    value as unknown as number
  );

/**
 * **Lossy.** Converts money to a float, for chart *geometry* only — a pixel is not a cent, and a bar's height
 * has no audit trail.
 *
 * Never use this for a value that is displayed as money, compared, summed, or written back. If you find yourself
 * reaching for it to add two amounts: the server owes you that total. This module exports no `add`, `sub` or
 * `mul` for exactly that reason — withholding the operators is how "the backend calculates, the frontend
 * displays" stays true instead of merely being asked for.
 */
export const moneyToNumberForChartGeometryOnly = (value: string): number => Number(value);
